import type { Validation } from '@m/types/api';

export type Location = {
  allHoursOfOperation: OperationHours[];
  assetProfile?: string; // ENUM
  capacity?: number;
  city: string;
  contractType?: string;
  hoursOfOperation: {
    today: BaseOperationHours;
  };
  id: number;
  internalName?: string;
  internetAvailability?: string;
  latitude: number;
  locationType?: string; // ENUM
  longitude: number;
  name: string;
  operatorId: number;
  operatorSiteId: number;
  sitePlatformSettings: SitePlatformSettings;
  squareFeet?: number;
  squareLocationId: string;
  state: string; // ENUM
  status: string; // ENUM
  street: string;
  taxPercent?: number | null;
  timezone?: string;
  uuid: string;
  zip: string;
  zoneId?: string;
};

export interface OperationHours extends BaseOperationHours {
  dayOfWeek: string;
}

interface BaseOperationHours {
  close: string;
  open: string;
}

export type UpdateHoursPayload = {
  friday?: OperationHours;
  monday?: OperationHours;
  saturday?: OperationHours;
  sunday?: OperationHours;
  thursday?: OperationHours;
  tuesday?: OperationHours;
  wednesday?: OperationHours;
};

export type EffectiveStartDateOptionView = {
  dateDisplay: string;
  dateValue: string | null;
};

export type SiteEffectiveSubscriptionStartDateView = {
  currentEffectiveStartDate: EffectiveStartDateOptionView;
  currentOptionIndex: number;
  effectiveStartDateOptions: EffectiveStartDateOptionView[];
  isEditable: boolean;
};

export type SitePlatformSettings = {
  autoChargeVisitConfig: {
    laneConfigSummary?: {
      autoChargeOnExitLanes: number;
      disabledLanes: number;
      totalLanes: number;
    };
  };
  autoCloseVisitConfig: {
    autoCloseDelay: number;
    enabled: boolean;
  };
  consumerVisible: boolean;
  consumerVisibleOverride: boolean;
  disableGuestRequests?: boolean;
  disableTextToPayFallbackVendEnabled?: boolean;
  hospitalityStatusEnabled: boolean;
  missingRatesEnabled?: boolean;
  noTechEnabled: boolean;
  seamlessExitEnabled: boolean;
  selfParkEnabled: boolean;
  siteEffectiveSubscriptionStartDateView: SiteEffectiveSubscriptionStartDateView;
  status: LocationSettingStatusEnum;
  subscriptionVisible: boolean;
  subscriptionVisibleOverride: boolean;
  tippingEnabled: boolean;
  uberEnabled: boolean;
  uberVisible: boolean;
  valetEnabled: boolean;
  valetOffsiteEnabled?: boolean;
  valetOnsiteEnabled?: boolean;
};

export type LocationValidation = {
  active: boolean;
  addedAt: number;
  id: number;
  updatedAt: number;
  vendorName: string;
};

export type LocationValidationV2 = {
  enterpriseId: number | null;
  enterpriseLocationId: number | null;
  validation: {
    active: boolean;
    addedAt: number;
    id: number;
    name: string | null;
    updatedAt: number;
    validationPlan: Validation;
    vendorName: string;
  };
};

export type LocationGateActions = {
  alwaysAutoVend: boolean;
  memberAccess: boolean;
  subscriberAccess: boolean;
  textToPay: boolean;
  textToVend: boolean;
};

export type LocationLane = {
  autoChargeVisitConfig: AutoChargeVisitConfig;
  cameraEnabled: boolean;
  direction: string;
  gateActive: boolean;
  hasCamera: boolean;
  hasGate: boolean;
  id: number;
  name: string;
  siteId: number;
  staffingConfig?: StaffingConfig;
  textShortCode: string | null;
};

export enum StaffingConfig {
  STAFFED = 'Staffed',
  UNSTAFFED = 'Unstaffed',
}

export enum AutoChargeVisitConfig {
  DISABLED = 'Disabled',
  ON_EXIT = 'OnExit',
}

export type GateActions = {
  automatic: boolean;
  nestedZone: boolean;
  parkingPass: boolean;
  reservation: boolean;
  signCodeCode: string;
  subscriber: boolean;
  textToPay: boolean;
  textToVend: boolean;
  valetExit: boolean;
  vendOnSuccessfulCharge: boolean;
};

export type GateStatus = {
  active: boolean;
  enabled: boolean;
  gateToken: string;
  gateType: {
    id: number;
    name: string;
  };
  id: number;
  laneId: number;
};

export type ControlDefaults = {
  pulseDelayMs: number;
  pulseLengthMs: number;
};

export type LocationLaneDetail = {
  edgeDevice?: {
    controlDefaults: ControlDefaults;
    deviceIp: string;
    deviceName: string;
    deviceUuid: string;
    gateNotes: string;
    gateUuid: string;
    relayNumber: number;
  };
  equipmentDetails?: {
    approachSettingType: string;
    cameraIp: string;
    computeIp: string;
    controlDefaults: ControlDefaults;
    deviceIp: string;
    deviceName: string;
    deviceUuid: string;
    enabled: boolean;
    gateNotes: string;
    gateUuid: string;
    id: number;
    linkedSiteId?: number;
    make: string;
    notes?: string;
    serial: string;
  } | null;
  gateActions?: GateActions;
  gateDetails?: {
    active: boolean;
    directedLaneId: number;
    gateToken: string;
    id: number;
    laneId: number;
  } | null;
  gateStatus?: GateStatus;
  laneDetails: {
    autoChargeVisitConfig?: AutoChargeVisitConfig;
    directedLaneId: number;
    direction: string;
    id: number;
    name: string;
    siteId: number;
    staffingConfig?: StaffingConfig;
  };
  signCode?: string | null;
};

export enum LocationSettingStatusEnum {
  ONBOARDING = 'Onboarding',
  INACTIVE = 'Inactive',
  ACTIVE = 'Active',
}

export enum LaneDirectionEnum {
  ENTRY = 'Entry',
  EXIT = 'Exit',
}

export const isExitLane = (laneDirection?: LaneDirectionEnum) =>
  laneDirection === LaneDirectionEnum.EXIT;
